import { Col, List, Radio, Row, Space, Typography } from 'antd';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { MouseEvent, useRef } from 'react';
import { Bar, Chart, getElementAtEvent } from 'react-chartjs-2';
import { Link, createSearchParams, useSearchParams } from 'react-router-dom';

import SearchParam from '../../components/SearchParam';
import { RecruitmentProcessCollection } from '../../types';

interface ScorePercentage {
  low: number;
  medium: number;
  high: number;
}

interface VacancyCandidateChartsProps {
  dashboardStatus?: RecruitmentProcessCollection['status'];
  matchPercentage?: ScorePercentage;
  ocpPercentage?: ScorePercentage;
  rawPotentialPercentage?: ScorePercentage;
  ocpMatchMatrix: number[][];
  ocpRawPotentialMatrix: number[][];
  selectedQuadrantDataSource?: RecruitmentProcessCollection[];
  selectedCount?: number;
  loading?: boolean;
}

const VacancyCandidateCharts = ({
  dashboardStatus,
  matchPercentage,
  ocpPercentage,
  rawPotentialPercentage,
  ocpMatchMatrix,
  ocpRawPotentialMatrix,
  selectedQuadrantDataSource,
  selectedCount,
  loading = false,
}: VacancyCandidateChartsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const chartRef = useRef<ChartJS>(null);

  const onClickChartQuadrant = (event: MouseEvent<HTMLCanvasElement>) => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    const element = getElementAtEvent(chart, event);

    if (element.length === 0) {
      return;
    }

    const datasetIndex = element[0].datasetIndex;
    const dataIndex = element[0].index;

    searchParams.set('selected_page', '1');

    switch (dataIndex) {
      case 0:
        searchParams.set('selected_ocp', 'LOW');
        break;
      case 1:
        searchParams.set('selected_ocp', 'MEDIUM');
        break;
      case 2:
        searchParams.set('selected_ocp', 'HIGH');
        break;
    }

    if (searchParams.get('matrix_type') === 'MATCH') {
      switch (datasetIndex) {
        case 0:
          searchParams.set('selected_match', 'LOW');
          searchParams.delete('selected_raw_potential');
          break;
        case 1:
          searchParams.set('selected_match', 'MEDIUM');
          searchParams.delete('selected_raw_potential');
          break;
        case 2:
          searchParams.set('selected_match', 'HIGH');
          searchParams.delete('selected_raw_potential');
          break;
      }
    } else if (searchParams.get('matrix_type') === 'RAW_POTENTIAL') {
      switch (datasetIndex) {
        case 0:
          searchParams.set('selected_raw_potential', 'LOW');
          searchParams.delete('selected_match');
          break;
        case 1:
          searchParams.set('selected_raw_potential', 'MEDIUM');
          searchParams.delete('selected_match');
          break;
        case 2:
          searchParams.set('selected_raw_potential', 'HIGH');
          searchParams.delete('selected_match');
          break;
      }
    }

    setSearchParams(searchParams);
  };

  const barChartBackgroundColor = ['#A44A3F', '#FFC400', '#007A50'];

  const matchBarChartData = {
    labels: [
      ['Baixo', 'abaixo de 15%'],
      ['Médio', 'entre 15% e 45%'],
      ['Alto', 'acima de 45%'],
    ],
    datasets: [
      {
        label: 'Nota',
        data: matchPercentage
          ? [matchPercentage.low, matchPercentage.medium, matchPercentage.high]
          : [],
        backgroundColor: barChartBackgroundColor,
        borderWidth: 1,
        datalabels: { offset: 3 },
        barThickness: 26,
      },
    ],
  };

  const ocpBarChartData = {
    labels: [
      ['Baixo', 'abaixo de 35%'],
      ['Médio', 'entre 35% e 55%'],
      ['Alto', 'acima de 55%'],
    ],
    datasets: [
      {
        label: 'Nota',
        data: ocpPercentage ? [ocpPercentage.low, ocpPercentage.medium, ocpPercentage.high] : [],
        backgroundColor: barChartBackgroundColor,
        borderWidth: 1,
        datalabels: { offset: 3 },
        barThickness: 26,
      },
    ],
  };

  const rawPotentialBarChartData = {
    labels: [
      ['Baixo', 'abaixo de 30%'],
      ['Médio', 'entre 30% e 50%'],
      ['Alto', 'acima de 50%'],
    ],
    datasets: [
      {
        label: 'Nota',
        data: rawPotentialPercentage
          ? [rawPotentialPercentage.low, rawPotentialPercentage.medium, rawPotentialPercentage.high]
          : [],
        backgroundColor: barChartBackgroundColor,
        borderWidth: 1,
        datalabels: { offset: 3 },
        barThickness: 26,
      },
    ],
  };

  const barChartOptions = {
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as 'end' | 'center' | 'start',
        align: 'end' as number | 'end' | 'bottom' | 'top' | 'left' | 'right' | 'center' | 'start',
        color: 'black',
        formatter(value: number) {
          return Math.round(value) + '%';
        },
      },
      tooltip: { enabled: false },
    },
    layout: { padding: { top: 20 } },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { stepSize: 25 },
      },
    },
  };

  const ocpFilter: { ocp_min: string; ocp_max: string } = { ocp_min: '', ocp_max: '' };
  const scoreFilter: {
    match_min: string;
    match_max: string;
    raw_potential_min: string;
    raw_potential_max: string;
  } = { match_min: '', match_max: '', raw_potential_min: '', raw_potential_max: '' };
  const matrixBorderColorMatrix = [
    ['white', 'white', 'white'],
    ['white', 'white', 'white'],
    ['white', 'white', 'white'],
  ];
  if (
    searchParams.get('selected_ocp') &&
    (searchParams.get('selected_match') || searchParams.get('selected_raw_potential'))
  ) {
    let ocpIndex;
    switch (searchParams.get('selected_ocp')) {
      case 'LOW':
        ocpFilter.ocp_max = '34.99';
        ocpIndex = 0;
        break;
      case 'MEDIUM':
        ocpFilter.ocp_min = '35';
        ocpFilter.ocp_max = '54.99';
        ocpIndex = 1;
        break;
      case 'HIGH':
        ocpFilter.ocp_min = '55';
        ocpIndex = 2;
        break;
    }
    let scoreIndex;
    if (searchParams.get('matrix_type') === 'MATCH') {
      switch (searchParams.get('selected_match')) {
        case 'LOW':
          scoreFilter.match_max = '14.99';
          scoreIndex = 0;
          break;
        case 'MEDIUM':
          scoreFilter.match_min = '15';
          scoreFilter.match_max = '44.99';
          scoreIndex = 1;
          break;
        case 'HIGH':
          scoreFilter.match_min = '45';
          scoreIndex = 2;
          break;
      }
    } else if (searchParams.get('matrix_type') === 'RAW_POTENTIAL') {
      switch (searchParams.get('selected_raw_potential')) {
        case 'LOW':
          scoreFilter.raw_potential_max = '29.99';
          scoreIndex = 0;
          break;
        case 'MEDIUM':
          scoreFilter.raw_potential_min = '30';
          scoreFilter.raw_potential_max = '49.99';
          scoreIndex = 1;
          break;
        case 'HIGH':
          scoreFilter.raw_potential_min = '50';
          scoreIndex = 2;
          break;
      }
    }
    if (typeof ocpIndex === 'number' && typeof scoreIndex === 'number') {
      matrixBorderColorMatrix[scoreIndex][ocpIndex] = 'grey';
    }
  }

  const matrixChartData = {
    labels: [
      ['Baixo', 'abaixo de 35%'],
      ['Médio', 'entre 35% e 55%'],
      ['Alto', 'acima de 55%'],
    ],
    datasets: [
      {
        label: 'Dataset 1', // Low Match/Raw Potential score
        data: [10, 10, 10], // Fixed values so that the bars have the same Height
        backgroundColor: ['#FFA39E', '#FFE381', '#F2B749'], // Low, Medium and High OCP score colors
        borderWidth: 2.5,
        borderSkipped: false,
        datalabels: { offset: 3 },
        barThickness: 'flex' as number | 'flex' | undefined,
        barPercentage: 1,
        categoryPercentage: 1,
        borderColor: matrixBorderColorMatrix[0],
      },
      {
        label: 'Dataset 2', // Medium Match/Raw Potential score
        data: [10, 10, 10], // Fixed values so that the bars have the same Height
        backgroundColor: ['#FFE381', '#95DE64', '#30870B'], // Low, Medium and High OCP score colors
        borderWidth: 2.5,
        borderSkipped: false,
        datalabels: { offset: 3 },
        barThickness: 'flex' as number | 'flex' | undefined,
        barPercentage: 1,
        categoryPercentage: 1,
        borderColor: matrixBorderColorMatrix[1],
      },
      {
        label: 'Dataset 3', // High Match/Raw Potential score
        data: [10, 10, 10], // Fixed values so that the bars have the same Height
        backgroundColor: ['#F2B749', '#30870B', '#007A50'], // Low, Medium and High OCP score colors
        borderWidth: 2.5,
        borderSkipped: false,
        datalabels: { offset: 3 },
        barThickness: 'flex' as number | 'flex' | undefined,
        barPercentage: 1,
        categoryPercentage: 1,
        borderColor: matrixBorderColorMatrix[2],
      },
    ],
  };

  const matrixChartOptions = {
    plugins: {
      legend: { display: false },
      datalabels: {
        textAlign: 'center' as 'end' | 'center' | 'start' | 'left' | 'right' | undefined,
        labels: {
          title: {
            font: {
              weight: 'bold' as number | 'bold' | 'normal' | 'bolder' | 'lighter' | undefined,
              size: 14,
            },
          },
        },
        color(context: Context) {
          if (
            (context.datasetIndex === 1 && context.dataIndex === 2) ||
            (context.datasetIndex === 2 && context.dataIndex >= 1)
          ) {
            return '#FFFFFF';
          }
          return '#41414D';
        },
        formatter(value: number, context: Context) {
          const scoreIndex = context.datasetIndex;
          const scoreMatrix =
            searchParams.get('matrix_type') === 'MATCH'
              ? ocpMatchMatrix
              : searchParams.get('matrix_type') === 'RAW_POTENTIAL'
              ? ocpRawPotentialMatrix
              : undefined;
          const ocpIndex = context.dataIndex;
          return scoreMatrix ? [scoreMatrix[scoreIndex][ocpIndex], 'candidatos'] : '-';
        },
      },
      tooltip: { enabled: false },
    },
    layout: { padding: { top: 20 } },
    scales: {
      y: {
        stacked: true,
        ticks: {
          stepSize: 5,
          callback(value: string | number, index: number) {
            let yLabel;
            if (searchParams.get('matrix_type') === 'MATCH') {
              switch (index) {
                case 1:
                  yLabel = ['Baixo', 'abaixo de 15%'];
                  break;
                case 3:
                  yLabel = ['Médio', 'entre 15% e 45%'];
                  break;
                case 5:
                  yLabel = ['Alto', 'acima de 45%'];
                  break;
              }
            } else if (searchParams.get('matrix_type') === 'RAW_POTENTIAL') {
              switch (index) {
                case 1:
                  yLabel = ['Baixo', 'abaixo de 30%'];
                  break;
                case 3:
                  yLabel = ['Médio', 'entre 30% e 50%'];
                  break;
                case 5:
                  yLabel = ['Alto', 'acima de 50%'];
                  break;
              }
            }
            return yLabel as string | number | string[] | number[] | null | undefined;
          },
        },
        title: {
          display: true,
          text:
            searchParams.get('matrix_type') === 'MATCH'
              ? 'Match'
              : searchParams.get('matrix_type') === 'RAW_POTENTIAL'
              ? 'Potencial Bruto'
              : '-',
          font: { weight: 'bold', size: 14 },
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Fit Cultural',
          font: { weight: 'bold', size: 14 },
        },
      },
    },
  };

  return (
    <>
      <Space direction="vertical" size="middle">
        <Row gutter={[32, 16]} style={{ height: 290 }}>
          <Col span={8}>
            <Typography.Paragraph style={{ fontSize: 12 }}>
              <Typography.Title level={5} style={{ color: '#41414D' }}>
                Match com a vaga
              </Typography.Title>
              Número de candidatos que possuem Match com a vaga, calculado com base nos testes
              psicométricos e arquétipo escolhido.
            </Typography.Paragraph>
            <Bar
              style={{ position: 'absolute', bottom: 0 }}
              data={matchBarChartData}
              plugins={[ChartDataLabels]}
              options={barChartOptions}
            />
          </Col>
          <Col span={8}>
            <Typography.Paragraph style={{ fontSize: 12 }}>
              <Typography.Title level={5} style={{ color: '#41414D' }}>
                Fit cultural com a empresa
              </Typography.Title>
              Número de candidatos que possuem Fit Cultural com a empresa, calculado com base no
              teste de estilos de trabalho e parâmetros de cultura da empresa.
            </Typography.Paragraph>
            <Bar
              style={{ position: 'absolute', bottom: 0 }}
              data={ocpBarChartData}
              plugins={[ChartDataLabels]}
              options={barChartOptions}
            />
          </Col>
          <Col span={8}>
            <Typography.Paragraph style={{ fontSize: 12 }}>
              <Typography.Title level={5} style={{ color: '#41414D' }}>
                Potencial bruto
              </Typography.Title>
              Número de candidatos de acordo com a nota de Potencial bruto, calculada com base nos
              testes psicométricos.
            </Typography.Paragraph>
            <Bar
              style={{ position: 'absolute', bottom: 0 }}
              data={rawPotentialBarChartData}
              plugins={[ChartDataLabels]}
              options={barChartOptions}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Row>
            <Col>
              <Typography.Paragraph style={{ fontSize: 12 }}>
                <Typography.Title level={5} style={{ color: '#41414D' }}>
                  Matriz de candidatos
                </Typography.Title>
                Matriz que relaciona Match com Fit Cultural e Potencial Bruto e Fit Cultural. Clique
                no quadrante escolhido para ver a listagem de candidatos.
              </Typography.Paragraph>
              <SearchParam
                name="matrix_type"
                onSetSearchParams={{
                  selected_ocp: '',
                  selected_match: '',
                  selected_raw_potential: '',
                }}
              >
                <Radio.Group>
                  <Radio value="MATCH">
                    <Typography.Text strong style={{ fontSize: 12 }}>
                      Match X Fit Cultural
                    </Typography.Text>
                  </Radio>
                  <Radio value="RAW_POTENTIAL">
                    <Typography.Text strong style={{ fontSize: 12 }}>
                      Potencial Bruto X Fit Cultural
                    </Typography.Text>
                  </Radio>
                </Radio.Group>
              </SearchParam>
            </Col>
          </Row>
          <Col span={17}>
            <Chart
              data={matrixChartData}
              plugins={[ChartDataLabels]}
              ref={chartRef}
              type="bar"
              onClick={onClickChartQuadrant}
              options={matrixChartOptions}
            />
          </Col>
          <Col span={7}>
            <List
              bordered
              size="small"
              style={{ fontSize: 12, height: '100%' }}
              pagination={{
                size: 'small',
                pageSize: 8,
                showSizeChanger: false,
                total: selectedCount,
                onChange: (page) => {
                  searchParams.set('selected_page', `${page || 1}`);
                  setSearchParams(searchParams);
                },
                current: parseInt(searchParams.get('selected_page') || '1', 10),
              }}
              loading={loading}
              header={<Typography.Text strong>Listagem de candidatos</Typography.Text>}
              footer={
                <Link
                  to={{
                    pathname: './../analysis',
                    search: createSearchParams({
                      stages: '',
                      status: dashboardStatus ?? '',
                      ...ocpFilter,
                      ...scoreFilter,
                    }).toString(),
                  }}
                  style={{
                    pointerEvents: (selectedCount ?? 0) > 0 ? 'auto' : 'none',
                  }}
                >
                  <Typography.Link disabled={selectedCount === 0}>
                    Ver listagem na página de análise de candidatos
                  </Typography.Link>
                </Link>
              }
              dataSource={selectedQuadrantDataSource}
              renderItem={(item) => (
                <List.Item>
                  <Typography.Text>{item.candidate.name}</Typography.Text>
                </List.Item>
              )}
              locale={{
                emptyText: 'Clique em um dos quadrantes ao lado para ver a listagem de candidatos.',
              }}
            />
          </Col>
        </Row>
      </Space>
    </>
  );
};

export default VacancyCandidateCharts;
